import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FiHeart, FiStar, FiMapPin } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { toast } from 'react-toastify';

const ApartmentCard = ({ apartment, onSaveToggle }) => {
  const { isUserAuthenticated } = useAuth();
  const [isSaved, setIsSaved] = useState(apartment.isSaved || false);
  const [currentImage, setCurrentImage] = useState(0);

  const handleSave = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!isUserAuthenticated()) {
      toast.info('Please login to save apartments');
      return;
    }

    try {
      if (isSaved) {
        await api.removeSavedApartment(apartment._id);
        toast.success('Removed from saved');
      } else {
        await api.saveApartment(apartment._id);
        toast.success('Saved to favorites');
      }
      setIsSaved(!isSaved);
      if (onSaveToggle) onSaveToggle(apartment._id, !isSaved);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const images = apartment.images?.length > 0
    ? apartment.images
    : ['/images/placeholder-apartment.jpg'];

  const styles = {
    card: {
      borderRadius: '12px',
      overflow: 'hidden',
      background: 'white',
      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
      cursor: 'pointer'
    },
    imageContainer: {
      position: 'relative',
      aspectRatio: '4/3',
      overflow: 'hidden'
    },
    image: {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      transition: 'transform 0.3s ease'
    },
    saveBtn: {
      position: 'absolute',
      top: '12px',
      right: '12px',
      width: '36px',
      height: '36px',
      borderRadius: '50%',
      background: 'white',
      border: 'none',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
      transition: 'transform 0.2s ease',
      zIndex: 10
    },
    badge: {
      position: 'absolute',
      top: '12px',
      left: '12px',
      background: 'white',
      padding: '6px 12px',
      borderRadius: '20px',
      fontSize: '0.75rem',
      fontWeight: '600',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
    },
    dots: {
      position: 'absolute',
      bottom: '12px',
      left: '50%',
      transform: 'translateX(-50%)',
      display: 'flex',
      gap: '6px'
    },
    dot: {
      width: '6px',
      height: '6px',
      borderRadius: '50%',
      background: 'rgba(255,255,255,0.5)',
      cursor: 'pointer'
    },
    dotActive: {
      background: 'white'
    },
    content: {
      padding: '16px'
    },
    location: {
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      color: '#555',
      fontSize: '0.875rem',
      marginBottom: '4px'
    },
    title: {
      fontSize: '1rem',
      fontWeight: '600',
      color: '#222',
      marginBottom: '4px',
      display: '-webkit-box',
      WebkitLineClamp: 1,
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden'
    },
    type: {
      color: '#555',
      fontSize: '0.875rem',
      marginBottom: '8px'
    },
    footer: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginTop: '8px'
    },
    price: {
      fontSize: '1rem'
    },
    priceValue: {
      fontWeight: '700'
    },
    priceNight: {
      color: '#555',
      fontWeight: '400'
    },
    rating: {
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      fontSize: '0.875rem'
    }
  };

  return (
    <Link to={`/apartments/${apartment.slug}`} style={{ textDecoration: 'none' }}>
      <div
        style={styles.card}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-4px)';
          e.currentTarget.style.boxShadow = '0 12px 24px rgba(0,0,0,0.12)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = 'none';
        }}
      >
        <div style={styles.imageContainer}>
          <img
            src={images[currentImage]}
            alt={apartment.name}
            style={styles.image}
          />

          <button
            style={styles.saveBtn}
            onClick={handleSave}
            onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
          >
            <FiHeart
              style={{
                color: isSaved ? '#1a3a5c' : '#222',
                fill: isSaved ? '#1a3a5c' : 'none'
              }}
            />
          </button>

          {apartment.featured && (
            <span style={styles.badge}>Featured</span>
          )}

          {images.length > 1 && (
            <div style={styles.dots}>
              {images.slice(0, 5).map((_, idx) => (
                <span
                  key={idx}
                  style={{
                    ...styles.dot,
                    ...(idx === currentImage ? styles.dotActive : {})
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setCurrentImage(idx);
                  }}
                />
              ))}
            </div>
          )}
        </div>

        <div style={styles.content}>
          <div style={styles.location}>
            <FiMapPin size={14} />
            <span>{apartment.location?.name || apartment.address?.city}</span>
          </div>

          <h3 style={styles.title}>{apartment.name}</h3>

          <p style={styles.type}>
            {apartment.type} &middot; {apartment.bedrooms} bed{apartment.bedrooms !== 1 ? 's' : ''} &middot; {apartment.maxGuests} guest{apartment.maxGuests !== 1 ? 's' : ''}
          </p>

          <div style={styles.footer}>
            <div style={styles.price}>
              <span style={styles.priceValue}>£{apartment.pricePerNight}</span>
              <span style={styles.priceNight}> / night</span>
            </div>

            {apartment.reviewCount > 0 && (
              <div style={styles.rating}>
                <FiStar style={{ fill: '#1a3a5c', color: '#1a3a5c' }} />
                <span style={{ fontWeight: '600' }}>{apartment.averageRating?.toFixed(1)}</span>
                <span style={{ color: '#555' }}>({apartment.reviewCount})</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
};

export default ApartmentCard;
